import * as React from 'react'

import { BookkeepersManagementIncidenceDocument } from 'generated/resources/graphql'
import type { BookkeepersManagementIncidenceQuery } from 'generated/resources/graphql'
import { unionBy } from 'lodash'
import { observable } from 'mobx'

import { useQuery } from 'modules/core/hooks/useQuery'

import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { useMessageFilterContext } from 'modules/bookkeepers/lib/MessageFilterContext'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'

const INCIDENCE_PER_PAGE = 100

export type IncidenceQueryData = NonNullable<
  BookkeepersManagementIncidenceQuery['bookkeepersManagement']
>['incidences'][number]

const useGetIncidences = ({
  messageIncidencesOnly,
  filterTypeFromDashboard,
}: {
  messageIncidencesOnly?: boolean
  filterTypeFromDashboard?: string
} = {}) => {
  const [incidences, setIncidences] = React.useState<IncidenceQueryData[]>(observable([]))
  const incidenceFilters = useIncidenceFilterContext()
  const messageFilters = useMessageFilterContext()
  const {
    page,
    filters,
    parsedFilterName,
    filterDate,
    attribute,
    debouncedSearch,
    direction,
    containsMessage,
  } = messageIncidencesOnly ? messageFilters : incidenceFilters

  const legalEntitiesIds = filters.legal_entity_id
    ? filters.legal_entity_id.split(',').map((le) => +le)
    : null
  const typeFilter = filterTypeFromDashboard ? [filterTypeFromDashboard] : parsedFilterName
  const customLeaveName = filters.custom_leave_name?.split(',')

  const isBookkeeperView = useBookkeeperView()
  const { data, loading, refetch } = useQuery(BookkeepersManagementIncidenceDocument, {
    variables: {
      type: typeFilter,
      status: filters.status?.split(','),
      startsOn: filterDate.firstDate,
      endsOn: filterDate.lastDate,
      perPage: INCIDENCE_PER_PAGE,
      page: page,
      search: debouncedSearch,
      direction,
      sortBy: attribute,
      legalEntitiesIds,
      containsMessage: containsMessage,
      customLeaveName,
    },
    context: {
      isBookkeeperView,
    },
    fetchPolicy: 'network-only',
  })

  const incidencesFetched = data?.bookkeepersManagement?.incidences

  if (loading && page == 1 && incidences.length) {
    setIncidences(observable([]))
  }

  if (!loading) {
    if (incidencesFetched?.length && (page - 1) * INCIDENCE_PER_PAGE >= incidences.length) {
      setIncidences(observable([...incidences, ...incidencesFetched]))
    }
    // This case is for when we add manual updates and want to display it without reloading the page
    else if (
      incidencesFetched?.length &&
      page * INCIDENCE_PER_PAGE >= incidences.length &&
      incidencesFetched.length > incidences.length
    ) {
      const uniqueIncidences = unionBy(incidences, incidencesFetched, 'id')
      setIncidences(observable(uniqueIncidences))
    }
  }

  return { loading, incidences, refetch }
}

export default useGetIncidences
